import {useContext} from 'react'
import {FormControl, Stack} from '@primer/react-brand'
import {ConsentExperienceInput} from './ConsentExperienceInput'

import {FormContext} from '../../FormContext'
import {ConsentExperienceContext} from './ConsentExperienceContext'

export type ConsentExperienceProps = {
  /**
   * If `true`, the consent checkbox won't appear at all for countries that support implicit opt-in, and
   * consent will be implied if the user submits the form.
   *
   * If `false`, the checkbox will always appear and must be checked to give consent.
   *
   * Note: this prop will only have an effect in countries that support implicit opt-in (e.g. the US).
   * In other countries, the checkbox will always appear regardless of this prop's value.
   *
   * @default true
   */
  implicitOptIn?: boolean
}

/**
 * Requirements:
 * - Located inside a ConsentExperience.Provider (e.g. see App.tsx)
 * - Located alongside a CountrySelectField component
 */
export const ConsentExperience = ({implicitOptIn = true}: ConsentExperienceProps) => {
  const consentExperienceExamplesFields = ['first name', 'last name', 'company', 'email']
  const formContext = useContext(FormContext)
  const ctx = useContext(ConsentExperienceContext)

  const primaryConsentError = formContext?.errors['primaryConsent']
  const primaryConsentId = formContext?.formFields?.['primaryConsent']?.id

  return (
    <Stack direction="vertical" gap="condensed" padding="none">
      <FormControl
        id="form-field-consent-experience"
        validationStatus={typeof primaryConsentError === 'string' ? 'error' : undefined}
        fullWidth
      >
        <ConsentExperienceInput
          selectedCountry={ctx.selectedCountry ?? {name: '', alpha: ''}}
          emailSubscriptionSettingsLinkHref="/settings/emails/subscriptions/link-request/new"
          exampleFields={consentExperienceExamplesFields}
          fieldName="marketing_email_opt_in"
          privacyStatementHref="https://docs.github.com/articles/github-privacy-statement"
          implicitOptIn={implicitOptIn}
        />

        {typeof primaryConsentError === 'string' ? (
          <FormControl.Validation id={`${primaryConsentId}-validation`}>{primaryConsentError}</FormControl.Validation>
        ) : null}
      </FormControl>
    </Stack>
  )
}

try{ ConsentExperience.displayName ||= 'ConsentExperience' } catch {}